// @ts-strict-ignore
import { isNil } from 'lodash';
import { useContext } from 'react';
import { ScrollView } from 'react-native';
import { View } from '@shared/components/View';
import { Dropdown, DropdownButton } from '@shared/components/dropdown';
import { MBHover } from '@shared/components/hover';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextBody2, MBTextOverlineBold } from '@shared/components/typography';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import {
  DisplayableComponent,
  isCombinedTagsComponent,
  isDisplayableComponent,
  isEntityBatchComponent,
  isReferencedInComponent,
  isTagComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { LinkableWorkflow } from './RelatedCardLinkingOptions';

interface Props {
  workflow: LinkableWorkflow;
  selectedWorkflow: LinkableWorkflow;
  component: RelatedCardComponent;
  loading: boolean;
  onSelectFilteredComponent: (componentId: string) => void;
  onSelectLinkedComponent: (componentId: string) => void;
}

function ComponentsDropdown({
  selectedComponent,
  components,
  onSelectComponent,
  descriptionText,
  enabled,
}: {
  selectedComponent?: DisplayableComponent;
  components: DisplayableComponent[];
  onSelectComponent: (componentId: string) => void;
  descriptionText: string;
  enabled: boolean;
}) {
  const colors = useContext(ColorThemeContext);
  const hintColor = isNil(selectedComponent?.label)
    ? colors.foregroundInactive
    : colors.foregroundActive;
  return (
    <Dropdown
      style={{ padding: 0, margin: 0 }}
      button={({ onClick, isOpen }) => (
        <DropdownButton
          onClick={enabled ? onClick : null}
          open={isOpen}
          style={{ marginRight: grid(1) }}
        >
          <MBTextBody1 style={{ color: hintColor }}>
            {selectedComponent?.label ?? i18n('builder.relatedCardOptions.makeSelection')}
          </MBTextBody1>
        </DropdownButton>
      )}
      closeOnSelect
    >
      <View style={{ width: 278, maxHeight: 400 }} paddingVertical={2}>
        <ScrollView>
          <MBTextBody2 style={{ paddingBottom: grid(1), paddingHorizontal: grid(2) }}>
            {descriptionText}
          </MBTextBody2>
          {components.map(component => {
            return (
              <MBTouchableOpacity
                key={component.id}
                onPress={() => onSelectComponent(component.id)}
              >
                <MBHover
                  hoverStyle={enabled && { backgroundColor: colors.input.backgroundInactive }}
                  centerVertically
                >
                  <View row centerVertically paddingHorizontal={3} style={{ height: 40 }}>
                    {!isNil(component.icon) && (
                      <View paddingRight={1}>
                        <MBIconV2
                          name={component.icon as MBIconV2Name}
                          color={colors.foregroundActive}
                          size={grid(3)}
                        />
                      </View>
                    )}
                    <View fill centerVertically paddingLeft={1}>
                      <MBTextBody1 color={colors.foregroundActive}>{component.label}</MBTextBody1>
                    </View>
                    {component.id === selectedComponent?.id && (
                      <MBIconV2 name="check-filled" size={grid(4)} color={colors.accent} />
                    )}
                  </View>
                </MBHover>
              </MBTouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
    </Dropdown>
  );
}

export function RelatedCardFilterView({
  workflow,
  selectedWorkflow,
  component,
  onSelectFilteredComponent,
  onSelectLinkedComponent,
  loading,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const selectableWorkflowComponents =
    (
      selectedWorkflow?.components.filter(item => {
        if (
          isDisplayableComponent(item) &&
          !isCombinedTagsComponent(item) &&
          !isTagComponent(item) &&
          !isReferencedInComponent(item)
        ) {
          return item.isFilterable;
        }
        return false;
      }) as DisplayableComponent[]
    ).filter(c => !isEntityBatchComponent(c)) ?? [];

  const selectedFilterWorkflowComponentId =
    component?.listDefault?.filterByComponents[0]?.componentId;
  const selectedFilterWorkflowComponent = isNil(selectedFilterWorkflowComponentId)
    ? null
    : selectableWorkflowComponents.find(item => item.id === selectedFilterWorkflowComponentId);
  const components = workflow.components.filter(item => {
    return item.type === selectedFilterWorkflowComponent?.type;
  }) as DisplayableComponent[];

  const selectedLinkedComponentId =
    component?.listDefault?.filterByComponents[0]?.value
      ?.containsCurrentWorkflowTemplateComponentValue?.componentId;
  const selectedLinkedComponent = workflow.components.find(
    item => item.id === selectedLinkedComponentId,
  ) as DisplayableComponent;

  if (loading) {
    return <MBLoadingIndicator />;
  }

  return (
    <View padding={2}>
      <MBTextOverlineBold color={colors.foregroundInactive}>
        {i18n('builder.relatedCardOptions.filterBy')}
      </MBTextOverlineBold>
      <View paddingVertical={2}>
        <MBTextBody2 style={{ textAlign: 'left' }}>
          {i18n('builder.relatedCardOptions.fieldSelectionDescription')}
        </MBTextBody2>
      </View>
      <MBTextOverlineBold color={colors.foregroundInactive}>
        {i18n('builder.relatedCardOptions.fieldFromWorkflow', {
          workflowName: selectedWorkflow?.name ?? '',
        })}
      </MBTextOverlineBold>
      <View paddingVertical={1}>
        <ComponentsDropdown
          selectedComponent={selectedFilterWorkflowComponent}
          components={selectableWorkflowComponents}
          onSelectComponent={onSelectFilteredComponent}
          descriptionText={i18n('builder.relatedCardOptions.filterFieldSelectionHelperText', {
            workflowName: selectedWorkflow?.name ?? '',
          })}
          enabled={true}
        />
      </View>
      <View paddingVertical={1} style={{ backgroundColor: colors.grayBackground }} marginRight={1}>
        <MBTextBody1 style={{ paddingLeft: grid(1) }}>
          {i18n('builder.relatedCardOptions.matches')}
        </MBTextBody1>
      </View>
      <MBTextOverlineBold color={colors.foregroundInactive} style={{ paddingTop: grid(1) }}>
        {i18n('builder.relatedCardOptions.fieldFromCurrentWorkflow', {
          workflowName: workflow?.name ?? '',
        })}
      </MBTextOverlineBold>
      <View paddingVertical={1}>
        <ComponentsDropdown
          selectedComponent={selectedLinkedComponent}
          components={components}
          onSelectComponent={onSelectLinkedComponent}
          descriptionText={i18n('builder.relatedCardOptions.workflowFieldSelectionHelperText', {
            workflowName: workflow?.name ?? '',
          })}
          enabled={!isNil(selectedFilterWorkflowComponent)}
        />
      </View>
    </View>
  );
}
